import React, { useContext, useEffect, useState } from "react"
import { Grid} from "@mui/material";
import { addSession, setChecksTitle, showMessage, showOperationSummary } from "../../store/reducer";
import { getInitParamsByManagementConfig, getManagementInfo, useOperationStateEffect } from "../../helpers/operationHelper";
import { IOperationsStatus, ModalityType, Operations, Status, StatusMessages } from "../../types/FaceVerification";
import { initialFvParameters, useLocalStorage } from "../../helpers/useLocalStorage";
import { FVInitParams } from "neurotec-megamatcherid-management-client";
import { mmidInitParamsStorage } from "../../config/const";
import { AppContext } from "../../store/context";
import { RefVideoStream } from "../VideoStream/BiometricFaceStream";
import { useSnackbar } from "notistack";
import { useHistory } from "react-router-dom";
import OperationContainer from "../../components/Operations/styled/OperationContainer";
import OperationTitle from "../../components/Operations/styled/OperationTitle";
import FaceOperation from "../Enroll/Modalities/FaceOperation";
import Overview from "../Overview/Overview";
import CheckSideBar from "./components/CheckSideBar";
import { APIsContext } from "../../store/APIs/context";
import { startSession } from "../../helpers/webRTC";
import { NavigationContext } from "../../components/Navigation/store/context";

const Checks: React.FC = () => {

    const apisState = useContext(APIsContext).state
    const [options] = useLocalStorage<FVInitParams>(mmidInitParamsStorage, initialFvParameters);
    const { enqueueSnackbar } = useSnackbar();
    const [activeModality, setActiveModality] = useState<ModalityType | null>(null);
    const {state, dispatch} = useContext(AppContext)
    const navigationDispatch = useContext(NavigationContext).dispatch
    const history = useHistory();
    const [checkState, setCheckState] = useState<IOperationsStatus>(IOperationsStatus.IDLE);
    const childRef = React.useRef<RefVideoStream>(null)
    const [faceOperationUUID, setFaceOperationUUID] = useState("");
    const [liveAdjudication, setLiveAdjudication] = useState(false)

    useOperationStateEffect(
        state,
        apisState,
        dispatch,
        checkState,
        (status: IOperationsStatus) => setCheckState(status),
        activeModality,
        (modality: ModalityType | null) => setActiveModality(modality),
        navigationDispatch,
        liveAdjudication,
        enqueueSnackbar,
        faceOperationUUID,
        childRef.current?.cancelAdjudication,
    )

    useEffect(() => {
        const selectName = (livenessMode: boolean, icao: boolean) => {
            if (livenessMode) {
                if (icao) {
                    dispatch(setChecksTitle("Face Check Liveness & ICAO"))
                } else {
                    dispatch(setChecksTitle("Face Check Liveness"))
                }
            } else {
                if (icao)
                    dispatch(setChecksTitle("Face Check ICAO"))
                else {
                    dispatch(setChecksTitle("Face Checks Liveness & ICAO"))
                }
            }
        }

        const fetchManagementInfo = async () => {
            try {
                const managementInfo = await getManagementInfo(apisState.ManagementApi);
                if (!managementInfo) {
                    enqueueSnackbar("Could not connect to the server", {variant: "error"})
                    return;
                }
                if (managementInfo.allowSettingsOverride) {     
                    if (options.livenessMode !== undefined && options.checkIcaoCompliance !== undefined)
                        selectName(options.livenessMode > 0, options.checkIcaoCompliance)
                } else {
                    selectName(managementInfo.livenessMode > 0, managementInfo.checkIcaoCompliance)
                }
            } catch (error) {
                console.log(error)
            }
        }

        fetchManagementInfo()
    }, [dispatch, options, enqueueSnackbar, apisState.ManagementApi])

    useEffect(() => {
        if (state.activeModalities.faceModality) {
            setActiveModality(prev => {
                if (prev == null) {
                    return ModalityType.FACE_MODALITY
                } else {
                    return prev
                }
            })
        }
    }, [state.activeModalities.faceModality, history])

    useEffect(() => {
        if (state.activeModalities.faceModality === false) {
            history.push("/")
        }
    })

    const restart = () => {
        dispatch(showOperationSummary(""));
        setCheckState(IOperationsStatus.IDLE)
    }

    const check = () => {
        if (options) {
            getManagementInfo(apisState.ManagementApi).then(res => {
                if (res == null) {
                    enqueueSnackbar("Could not connect to the server", {variant: "error"})
                    return;
                }
                setLiveAdjudication(res.liveAdjudication)
                let initParams = getInitParamsByManagementConfig(res, options);
                initParams.type = Operations.CHECKS;
                initParams.subjectId = "";
                startSession(apisState.JanusApi, Operations.CHECKS, initParams).then(res => {
                    setCheckState(IOperationsStatus.ACTIVE)
                    dispatch(addSession(res.data, initParams));
                }).catch(err => {
                    setCheckState(IOperationsStatus.IDLE)
                    console.log(err)
                    dispatch(showMessage({ message: "Unable to create session: " + err.message, type: "error" }))
                });
            }).catch(e => {
                enqueueSnackbar(e.message, {variant: "error"})
            })
        } else {
            setCheckState(IOperationsStatus.IDLE)
            dispatch(showMessage({ message: "Options must be specified", type: "error" }));
        }
    }

    const getName = (): string => {
        switch (activeModality) {
            case ModalityType.FACE_MODALITY:
                return state.checksTitle
            case ModalityType.OVERVIEW:
                return "Overview"
            default: 
                return state.checksTitle
        }
    }

    const getActiveState = () => {
        switch (activeModality) {
            case ModalityType.FACE_MODALITY:
                return (
                    <FaceOperation
                        setCheckState={setCheckState}
                        childRef={childRef}
                        setOperationUUID={setFaceOperationUUID}
                        overwriteSuccess={liveAdjudication ? {message: StatusMessages[Status.ADJUDICATION_PENDING], type: "info"} : undefined}
                    />
                )
            case ModalityType.OVERVIEW:
                return (
                    <Overview
                        faceOperationUUID={faceOperationUUID}
                        voiceOperationUUID={""}
                    />
                )
        }
    }

    return (
        <OperationContainer maxWidth={false}>
            <Grid container style={{paddingLeft: "0px", paddingRight: "0px"}}>
                <OperationTitle>
                    {getName()}
                </OperationTitle>
                {getActiveState()}
                <CheckSideBar
                    checkState={checkState}
                    activeModality={activeModality}
                    start={check}
                    restart={restart}
                    cancel={childRef.current?.cancel}
                />
            </Grid>
        </OperationContainer>
    );
}

export default Checks;



